import { Box, RadioGroup, Stack, Text } from "@chakra-ui/react";
import Joi from "joi";
import { useEffect } from "react";

import {
  advertiserType as advertiserTypeConfig,
  availabilityType as availabilityTypeConfig,
  offerType as offerTypeConfig,
} from "@ud/config/prodeje/config";
import { IconCalendar, IconCoins, IconDocument } from "@ud/icons";
import { updateData } from "@ud/store/insertForm/insertSlice";
import Input from "components/Chakra/AnimatedInput";
import NewDatePicker from "components/Chakra/NewDatePicker";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import { useOfferInsert } from "contexts/InsertOffer";
import { useTracking } from "contexts/Tracking";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useRouter from "hooks/useRouter";
import { Card } from "../components/SectionCards";
import { SectionTitle } from "../components/SectionTitle";
import { COMMISSION_TYPE } from "./types";

type IPrice = {
  buttonNextText?: string;
  buttonPrevText?: string;
};

type IFormValue = {
  price: number;
  monthlyFeesPrice: number;
  // priceUnit: string;
  availability: string;
  specificDate: string;
  dateFromVisitation: string;
  dateToVisitation: string;
  priceCommission: string;
  descriptionPrice: string;
  depositPrice: number;
};

const formSchema = Joi.object({
  price: Joi.number().required(),
  monthlyFeesPrice: Joi.number().allow(""),
  depositPrice: Joi.number().allow(""),
  // priceUnit: Joi.string().required(),
  availability: Joi.string().required(),
  specificDate: Joi.string()
    .isoDate()
    .when("availability", {
      is: availabilityTypeConfig.date.id,
      then: Joi.required(),
      otherwise: Joi.allow(""),
    }),
  dateFromVisitation: Joi.string().isoDate(),
  dateToVisitation: Joi.string().isoDate(),
  priceCommission: Joi.string().required(),
});

const PriceForm: React.FC<IPrice> = ({ buttonNextText, buttonPrevText }) => {
  const { navigateTo } = useRouter();
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const insertReduxData = useAppSelector((e) => e.insertForm.data);
  const typeOffer =
    (useAppSelector(
      (state) => state.insertForm.data.typeOffer,
    ) as keyof typeof offerTypeConfig) || offerTypeConfig.coliving.id;

  const { nextStep, updateStep, getFirstStep } = useOfferInsert();

  const isMissingRequiredValue = (
    value: string | Array<string> | number | undefined | boolean | null,
  ): boolean => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return (
      value === undefined || value === null || value === "" || value === false
    );
  };

  const getPriceCommission = () => {
    if (insertReduxData.priceCommission === undefined) {
      return insertReduxData.typeInsert ===
        advertiserTypeConfig.realEstateBroker.id
        ? "COMMISSION"
        : "NO_COMMISSION";
    }

    return insertReduxData.priceCommission ? "COMMISSION" : "NO_COMMISSION";
  };

  const { handleSubmit, registerWithError, watch, setValue, errors } =
    useForm<IFormValue>(formSchema, {
      keepDataOnSuccess: true,
      defaultValues: {
        // priceUnit:
        //   insertReduxData.priceUnit || priceUnitTypeConfig.perRealEstate.id,
        availability:
          insertReduxData.availability || availabilityTypeConfig.asap.id,
        dateFromVisitation: insertReduxData.dateFromVisitation || undefined,
        dateToVisitation: insertReduxData.dateToVisitation || undefined,
        price: insertReduxData.price ?? undefined,
        specificDate: insertReduxData.specificDate || undefined,
        monthlyFeesPrice: insertReduxData.monthlyFeesPrice ?? undefined,
        depositPrice: insertReduxData.depositPrice ?? undefined,
        descriptionPrice: insertReduxData.descriptionPrice || undefined,
        priceCommission: getPriceCommission(),
      },
    });

  // const watchPriceUnit = watch("priceUnit");
  const watchPrice = watch("price");
  const watchAvailability = watch("availability");
  const watchSpecificDate = watch("specificDate");
  const watchDateFromVisitation = watch("dateFromVisitation");
  const watchDateToVisitation = watch("dateToVisitation");
  const watchCommission = watch("priceCommission");

  const checkIsBtnDisable = (): boolean => {
    const requiredArray: Array<
      string | Array<string> | number | undefined | boolean
    > = [watchPrice, watchAvailability];
    if (typeOffer !== "sale") {
      requiredArray.push(watchCommission);
    }

    if (watchAvailability === availabilityTypeConfig.date.id) {
      requiredArray.push(watchSpecificDate);
    }

    return requiredArray.some(isMissingRequiredValue);
  };

  const handleData: OnFormSubmitType<IFormValue> = ({
    availability,
    dateFromVisitation,
    dateToVisitation,
    price,
    // priceUnit,
    specificDate,
    priceCommission,
    descriptionPrice,
    monthlyFeesPrice,
    depositPrice,
  }) => {
    dispatch(
      updateData({
        data: {
          availability: availability || undefined,
          dateFromVisitation: dateFromVisitation || undefined,
          dateToVisitation: dateToVisitation || undefined,
          price: price ?? undefined,
          monthlyFeesPrice: monthlyFeesPrice ?? undefined,
          // priceUnit: priceUnit || undefined,
          specificDate: specificDate || undefined,
          priceCommission: priceCommission === "COMMISSION",
          descriptionPrice: descriptionPrice || undefined,
          depositPrice: depositPrice ?? undefined,
        },
      }),
    );
    updateStep("finished");
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "7. krok – Cena",
    });
    const step = nextStep();
    navigateTo(step.url, {
      id: step.query?.id,
      status: step.query?.status,
      paidId: step.query?.paidId,
    });
  };

  useEffect(() => {
    if (!insertReduxData.typeInsert) {
      const step = getFirstStep();
      navigateTo(step.url, {
        id: step.query?.id,
        status: step.query?.status,
        paidId: step.query?.paidId,
      });
    }
  }, [getFirstStep, insertReduxData.typeInsert, navigateTo]);

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "7. krok – Cena",
      }),
    [trackEvent],
  );

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Stack textAlign="left" spacing={0}>
        <Stack
          direction={{ base: "column", md: "row" }}
          columnGap="10px"
          spacing={0}
        >
          <Stack flex={1}>
            <SectionTitle
              title={typeOffer === "sale" ? "Prodejní cena" : "Nájemné měsíčně"}
              titlePaddingProps={{
                pb: typeOffer === "sale" ? "8px" : "0px",
              }}
              icon={<IconCoins boxSize="16px" />}
              isRequired
            />
            <Input
              type="number"
              rightElement={<Text>Kč</Text>}
              {...registerWithError("price")}
              w="100%"
            />
            <Text
              sx={{
                marginTop: "-5px !important",
              }}
              variant="small_1"
              lineHeight="16px"
            >
              Pokud zadáte 0, bude jako cena uvedeno „Info o ceně u majitele.
            </Text>
          </Stack>
          {typeOffer === "sale" ? (
            <></>
          ) : (
            // <Stack flex={1} spacing="16px">
            //   <SectionTitle
            //     title="Jednotka ceny"
            //     isRequired
            //     titlePaddingProps={{
            //       pb: "0px",
            //     }}
            //     icon={<IconCoins boxSize="16px" />}
            //   />
            //   <Select
            //     backgroundColor="ud-white"
            //     {...registerWithError("priceUnit")}
            //     icon={<IconDropdown />}
            //     iconSize="12px"
            //     value={watchPriceUnit}
            //   >
            //     {Object.entries(priceUnitTypeConfig).map((item) => (
            //       <option key={item[0]} value={item[1].id}>
            //         {item[1].translationId}
            //       </option>
            //     ))}
            //   </Select>
            // </Stack>
            <Stack flex={1}>
              <SectionTitle
                title="Jistota"
                titlePaddingProps={{ pt: { base: "16px", md: "0px" } }}
                description="150 zn."
                icon={<IconCoins boxSize="16px" />}
              />
              <Input {...registerWithError("depositPrice")} w="100%" />
            </Stack>
          )}
        </Stack>

        <Stack gap="16px" spacing={0}>
          <SectionTitle
            isRequired
            title={
              typeOffer === "sale" ? "Provize u nabídky" : "Provize k nájemnému"
            }
            titlePaddingProps={{
              pt: { base: "16px", md: "26px" },
              pb: "0px",
            }}
            icon={<IconCoins boxSize="16px" />}
          />
          <RadioGroup
            value={watchCommission}
            style={{ display: "flex", flexDirection: "row", gap: "10px" }}
          >
            <Stack direction="row" flex={1}>
              {Object.entries(COMMISSION_TYPE).map((item) => (
                <Card
                  key={item[0]}
                  id={item[0]}
                  item={item[1]}
                  nameValue="priceCommission"
                  setValue={setValue}
                  isHighlighted={item[0] === watchCommission}
                  cardStackCss={{ flex: 1 }}
                />
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
        <Stack
          spacing={0}
          direction={{ base: "column", md: "row" }}
          rowGap={{ base: "0", md: "10px" }}
          columnGap={{ base: "10px", md: "10px" }}
        >
          <Box flex={1}>
            <SectionTitle
              title="Náklady na bydlení"
              titlePaddingProps={{
                pt: { base: "32px", md: "40px" },
                pb: "16px",
              }}
              description="150 zn."
              icon={<IconCoins boxSize="16px" />}
            />
            <Input {...registerWithError("monthlyFeesPrice")} w="100%" />
          </Box>
          <Box flex={1}>
            <SectionTitle
              title="Poznámka k ceně"
              titlePaddingProps={{
                pt: { base: "16px", md: "40px" },
                pb: "16px",
              }}
              description="150 zn."
              icon={<IconDocument boxSize="16px" fillWith="ud-grey-600" />}
            />
            <Input {...registerWithError("descriptionPrice")} w="100%" />
          </Box>
        </Stack>

        <SectionTitle
          isRequired
          title={typeOffer === "sale" ? "Dostupnost prodeje" : "K naštěhování"}
          icon={<IconCalendar boxSize="16px" fillWith="ud-grey-600" />}
          titlePaddingProps={{
            pt: { base: "16px", md: "24px" },
            pb: "16px",
          }}
        />
        <RadioGroup
          value={watchAvailability}
          sx={{
            display: "flex",
            flexDirection: { base: "column", md: "row" },
            gap: "10px",
          }}
        >
          <Stack flex={1} maxW={{ md: "50%" }}>
            {Object.entries(availabilityTypeConfig).map((item) => (
              <Card
                key={item[0]}
                id={item[0]}
                item={item[1]}
                nameValue="availability"
                setValue={setValue}
                isHighlighted={item[0] === watchAvailability}
                cardStackCss={{ flex: 1 }}
              />
            ))}
          </Stack>
          {watchAvailability === availabilityTypeConfig.date.id && (
            <Stack flex={1} justify="flex-end" spacing={0}>
              <NewDatePicker
                sx={{
                  backgroundColor: "ud-white",
                }}
                pb={0}
                required
                isAnimatedInput={false}
                isFutureDate
                dateValue={
                  watchSpecificDate ? new Date(watchSpecificDate) : null
                }
                onDateChange={(date) => {
                  setValue("specificDate", date?.toISOString() || "");
                }}
                name="specificDate"
                error={errors.specificDate}
              />
            </Stack>
          )}
        </RadioGroup>
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={0}
          rowGap={{ base: "0", md: "10px" }}
          columnGap={{ base: "10px", md: "10px" }}
        >
          <Stack flex={1} spacing={0}>
            <SectionTitle
              icon={<IconCalendar boxSize="16px" fillWith="ud-grey-600" />}
              title="Datum prohlídky od"
            />
            <NewDatePicker
              sx={{
                backgroundColor: "ud-white",
              }}
              pb={0}
              required
              isFutureDate
              isAnimatedInput={false}
              dateValue={
                watchDateFromVisitation
                  ? new Date(watchDateFromVisitation)
                  : null
              }
              onDateChange={(date) => {
                setValue("dateFromVisitation", date?.toISOString() || "");
              }}
              name="dateFromVisitation"
              error={errors.dateFromVisitation}
            />
          </Stack>
          <Stack flex={1} spacing={0}>
            <SectionTitle
              title="Datum prohlídky do"
              icon={
                <IconCalendar
                  boxSize="16px"
                  fill="ud-grey-600"
                  color="ud-grey-600"
                />
              }
            />
            <NewDatePicker
              sx={{
                backgroundColor: "ud-white",
              }}
              pb={0}
              isFutureDate
              isAnimatedInput={false}
              required
              dateValue={
                watchDateToVisitation ? new Date(watchDateToVisitation) : null
              }
              onDateChange={(date) => {
                setValue("dateToVisitation", date?.toISOString() || "");
              }}
              name="dateToVisitation"
              error={errors.dateToVisitation}
            />
          </Stack>
        </Stack>
      </Stack>
      <StepButtons
        checkIsBtnDisable={checkIsBtnDisable}
        buttonPrevText={buttonPrevText}
        buttonNextText={buttonNextText}
        mt={[null, "24px"]}
      />
    </form>
  );
};

export default PriceForm;
